import React, { useState, useEffect, useMemo } from 'react';
import { FileDown } from 'lucide-react';
import { dsPackageApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import exportIndexSheetReports from './indexSheetReportPdf';
import StuiSelect from '../../components/common/StuiSelect';

/**
 * Index Sheet Package section of a Submittal Package.
 *
 * Two figures and one export, all taken from the package's own records:
 *   - Index Sheets: distinct index sheets the package's tags sit on;
 *   - Tags: distinct tags in the package.
 * Export renders the standard "Index Report - Tags" once per index sheet, narrowed to the
 * package's tags, as individual PDFs or as one combined PDF — see indexSheetReportPdf.js for
 * how it reuses the reports pipeline without touching it.
 *
 * Rendered hidden rather than unmounted when its header is collapsed, so an export in flight
 * keeps its progress and the chosen output mode survives a collapse.
 */

const OUTPUT_MODES = [
  { value: 'separate', label: 'Individual files (one PDF per index sheet)' },
  { value: 'combined', label: 'One combined PDF' },
];

const IndexSheetPackageSection = ({ pkg, hidden = false }) => {
  const { notify } = useNotifications();
  const dbContext = useDatabaseSafe();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('separate');
  const [progress, setProgress] = useState(null);

  // Re-read whenever the package object is replaced: a revision can add tags.
  useEffect(() => {
    if (!pkg?.id) { setRows(null); return undefined; }
    let cancelled = false;
    setError(null);
    dsPackageApi.records(pkg.id)
      .then(res => { if (!cancelled) setRows(res.data?.rows || []); })
      .catch(err => {
        if (!cancelled) {
          setRows(null);
          setError(err?.response?.data?.error || 'Failed to load the package records.');
        }
      });
    return () => { cancelled = true; };
  }, [pkg]);

  const { indexSheetIds, tagRowIds } = useMemo(() => {
    const sheets = new Set();
    const tags = new Set();
    (rows || []).forEach(r => {
      if (r.indexSheetId != null) sheets.add(r.indexSheetId);
      if (r.tagRowId != null) tags.add(r.tagRowId);
    });
    return { indexSheetIds: [...sheets], tagRowIds: [...tags] };
  }, [rows]);

  const runExport = async () => {
    setProgress('Starting…');
    try {
      const written = await exportIndexSheetReports({
        indexSheetIds,
        tagRowIds,
        sourceLabel: pkg.sourceLabel,
        separate: mode === 'separate',
        currentDb: dbContext?.currentDb,
        onProgress: setProgress,
      });
      if (written > 0) {
        notify.success(mode === 'separate'
          ? `${pkg.dsPackage}: ${written} index sheet PDF${written === 1 ? '' : 's'} exported`
          : `${pkg.dsPackage}: index sheets exported to one PDF`);
      } else {
        notify.info('No index sheet PDF was written.');
      }
    } catch (err) {
      notify.error(err?.response?.data?.error || err.message || 'Index sheet export failed.');
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="stui-col-24" hidden={hidden}>
      {error && <div className="stui-detail-empty">{error}</div>}

      <div className="stui-detail-grid">
        <div className="stui-field-group stui-col-6">
          <label>Index Sheets</label>
          <input className="stui-field-input" value={rows ? indexSheetIds.length : ''} readOnly />
        </div>
        <div className="stui-field-group stui-col-6">
          <label>Tags</label>
          <input className="stui-field-input" value={rows ? tagRowIds.length : ''} readOnly />
        </div>
      </div>

      <div className="stui-options-row">
        <div className="stui-sort-slot">
          <span className="stui-label">Export as:</span>
          <StuiSelect
            className="stui-select"
            value={mode}
            onChange={e => setMode(e.target.value)}
            disabled={!!progress}
          >
            {OUTPUT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </StuiSelect>
        </div>
        <button
          className="stui-btn stui-btn-primary"
          onClick={runExport}
          disabled={!!progress || indexSheetIds.length === 0}
          title={indexSheetIds.length === 0 ? 'No tag in this package has an index sheet' : undefined}
        >
          <FileDown size={16} />
          {progress ? 'Exporting…' : 'Export Index Sheets'}
        </button>
        {progress && <span className="stui-label">{progress}</span>}
      </div>
    </div>
  );
};

export default IndexSheetPackageSection;
